import axios from 'axios'

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true

const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

// Read token from localStorage (Remember Me) or sessionStorage (session-only)
const getToken = () => localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY)

const getAuthHeaders = () => {
  const token = getToken()
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  }
}

export const projectsApi = {
  /**
   * Fetch list of projects
   * @param {number} page - Page number (default: 1)
   * @param {number} perPage - Items per page (default: 50)
   * @returns {Promise<Object>} Projects list response
   */
  async fetchProjects(page = 1, perPage = 50) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/list/`,
        {
          headers: getAuthHeaders(),
          params: {
            page,
            per_page: perPage,
          },
        }
      )
      console.log('[projectsApi] Fetched projects:', response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error fetching projects:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch single project details
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Project details
   */
  async fetchProject(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error fetching project:', error)
      throw error
    }
  },

  /**
   * Create new project with file upload support
   * @param {string} name - Project name (required)
   * @param {string} description - Project description (optional)
   * @param {string} overview - Project overview (optional)
   * @param {File} image - Cover photo file (optional)
   * @returns {Promise<Object>} Created project
   */
  /**
   * Resolve a license key to its tier + validity, or return the Basic
   * (free) tier when the key is empty. Empty response is a valid
   * "you're on the free tier" answer, not an error.
   * @param {string} licenseKey
   * @returns {Promise<{ valid: boolean, tier: {name, is_free, expires_at?, customer?}, message?: string }>}
   */
  async lookupLicense(licenseKey = '') {
    const token = getToken()
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/license/lookup/`,
      {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        params:  { key: licenseKey || '' },
      },
    )
    return response.data
  },

  async createProject(name, description = '', overview = '', image = null, licenseKey = '') {
    try {
      const formData = new FormData()
      formData.append('name', name)
      if (description) formData.append('description', description)
      if (overview) formData.append('overview', overview)
      if (image) formData.append('image', image)
      if (licenseKey) formData.append('license_key', licenseKey)

      const token = getToken()
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/create/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating project:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Update project
   * @param {number} projectId - Project ID
   * @param {Object} projectData - Project data to update
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, projectData) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        projectData,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error updating project:', error)
      throw error
    }
  },

  /**
   * Upload (or replace) the project's Project Logo.
   * @param {number} projectId
   * @param {File} file - image file (PNG/JPG/GIF/WEBP/SVG, max 5 MB)
   * @returns {Promise<Object>} { success, logo } where logo is the new URL
   */
  async uploadProjectLogo(projectId, file) {
    const formData = new FormData()
    formData.append('logo', file)
    const token = getToken()
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/logo/`,
      formData,
      {
        // Let axios set the multipart boundary; just carry the bearer token.
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      }
    )
    return response.data
  },

  /**
   * Delete project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Response
   */
  async deleteProject(projectId) {
    try {
      const response = await axios.delete(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error('Error deleting project:', error)
      throw error
    }
  },

  /**
   * Request access to an existing project
   * @param {string} adminEmail - Email of the project admin
   * @param {string} message - Optional message for the admin
   * @returns {Promise<Object>} Request response with admin details
   */
  async requestProjectAccess(adminEmail, message = '') {
    try {
      const formData = new FormData()
      formData.append('admin_email', adminEmail)
      if (message) formData.append('message', message)

      const token = getToken()
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/request-access/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error requesting project access:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Create a Stripe Checkout Session for a plan change on a project.
   * Server returns { url } — caller redirects `window.location` there.
   * Bubbles the error message on the { error, code } shape the backend
   * emits (e.g. `stripe_not_configured`) so the caller can render a
   * targeted message.
   */
  async startSubscriptionCheckout({ projectId, tierName, period = 'monthly' }) {
    const token = getToken()
    const res   = await axios.post(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/checkout/`,
      { project_id: projectId, tier_name: tierName, period },
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type':  'application/json',
        },
      },
    )
    return res.data
  },

  /**
   * Cancel (or reactivate) a project's Stripe subscription. Cancel is
   * end-of-period: the customer keeps their plan until the current
   * billing period ends, then reverts to Basic — no refund. Pass
   * `reactivate: true` to undo a scheduled cancellation.
   */
  async cancelSubscription({ projectId, reactivate = false }) {
    const token = getToken()
    const res   = await axios.post(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/cancel/`,
      { project_id: projectId, reactivate },
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type':  'application/json',
        },
      },
    )
    return res.data
  },

  /**
   * Fetch the list of active tiers + cellular add-on plans from the
   * public tiers endpoint. The Manage Add-Ons modal uses the
   * `cellular_plans` array (already returned by /api/account/tiers/).
   */
  async fetchTiers() {
    const token = getToken()
    const res   = await axios.get(
      `${API_URL}${API_BASE_PATH}/account/tiers/`,
      { headers: { 'Authorization': token ? `Bearer ${token}` : '' } },
    )
    return res.data || {}
  },

  /**
   * Ask the backend to sync the project's default payment method from
   * Stripe right after returning from a setup-mode Checkout. Response
   * is `{method_type, method_last4}` — populates the Plans card
   * without waiting for the payment_method.attached webhook.
   */
  async refreshPaymentMethod({ projectId, sessionId }) {
    const token = getToken()
    const res   = await axios.post(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/refresh-payment/`,
      { project_id: projectId, session_id: sessionId || '' },
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type':  'application/json',
        },
      },
    )
    return res.data
  },

  /**
   * Start a Stripe Checkout Session in `mode='setup'` — collects a
   * card on the project's Stripe customer without charging. Backend
   * returns { url } for the caller to redirect the browser to.
   */
  async startPaymentSetup({ projectId }) {
    const token = getToken()
    const res   = await axios.post(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/setup-payment/`,
      { project_id: projectId },
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type':  'application/json',
        },
      },
    )
    return res.data
  },

  /**
   * Start a Stripe Checkout Session for a cellular data add-on.
   * `productKey` is the config-side identifier (e.g. `cellular_20mb`);
   * the backend resolves it to the correct Stripe Price ID for the
   * chosen period.
   */
  async startAddonCheckout({ projectId, productKey, period = 'monthly' }) {
    const token = getToken()
    const res   = await axios.post(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/checkout-addon/`,
      { project_id: projectId, product_key: productKey, period },
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type':  'application/json',
        },
      },
    )
    return res.data
  },

  /**
   * Fetch cached Stripe invoices for a project — powers the Billing
   * History modal on /plans. Latest 200 rows sorted by paid_at desc.
   */
  /**
   * Fetch the branded Fondriest PDF for a single invoice and trigger
   * a browser download. Authenticated with the JWT since the endpoint
   * enforces per-project access.
   */
  async downloadInvoicePdf(invoiceId, filenameHint) {
    const token = getToken()
    const res = await axios.get(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/invoice/${invoiceId}/download/`,
      {
        responseType: 'blob',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      },
    )
    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url  = window.URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `invoice-${filenameHint || invoiceId}.pdf`
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => window.URL.revokeObjectURL(url), 1500)
  },

  async fetchSubscriptionInvoices(projectId) {
    const token = getToken()
    const res   = await axios.get(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/invoices/`,
      {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        params:  { project_id: projectId, _t: Date.now() },
      },
    )
    return res.data?.invoices || []
  },

  /**
   * Fetch the ProjectSubscription row for a project.
   * Empty defaults on projects without a subscription.
   */
  async fetchSubscriptionStatus(projectId) {
    const token = getToken()
    const res   = await axios.get(
      `${API_URL}${API_BASE_PATH}/account/subscriptions/status/`,
      {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        params:  { project_id: projectId, _t: Date.now() },
      },
    )
    return res.data
  },

  /**
   * Fetch sites/stations for a specific project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} Array of sites with their devices and parameters
   */
  async fetchProjectSites(projectId) {
    // Anonymous callers (public portal viewers) can't hit the authed
    // /project/<id>/sites/ endpoint. When we're rendering under
    // /public/<pid>, skip the authed call entirely and use the
    // anonymous /public-portal/<pid>/sites/ endpoint instead so widget
    // auto-detect (Wind Rose etc.) works with real project data.
    const isPublic = typeof window !== 'undefined'
      && window.location?.pathname?.startsWith('/public/')
    if (isPublic) {
      try {
        const res = await axios.get(
          `${API_URL}${API_BASE_PATH}/public-portal/${projectId}/sites/`,
        )
        return res.data?.sites || res.data?.data || res.data || []
      } catch (error) {
        console.error('[projectsApi] public sites fetch failed:', error?.response?.status)
        return []
      }
    }
    try {
      // Fetch from backend API
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/sites/`,
        {
          headers: getAuthHeaders(),
        }
      )
      console.log(`[projectsApi] Fetched sites for project ${projectId}:`, response.data)
      return response.data.sites || response.data || []
    } catch (error) {
      console.error('[projectsApi] Error fetching project sites:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      // Return empty array instead of mock data - let backend data be the source of truth
      return []
    }
  },

  /**
   * Create a new site/station for a project
   * @param {number} projectId - Project ID
   * @param {Object} siteData - Site data
   * @param {string} siteData.name - Site name (required)
   * @param {string} siteData.description - Site description (optional)
   * @param {number} siteData.latitude - Site latitude (optional)
   * @param {number} siteData.longitude - Site longitude (optional)
   * @returns {Promise<Object>} Created site object
   */
  async createSite(projectId, siteData) {
    try {
      console.log('[projectsApi.createSite] Called with projectId:', projectId, 'type:', typeof projectId)
      
      // Backend endpoint: POST /api/project/site/ (ModelViewSet)
      // Django model uses 'project' field (ForeignKey), not 'project_id'
      const formData = new FormData()
      formData.append('name', siteData.name)
      // Send as 'project' (the actual field name in the Django model)
      formData.append('project', String(projectId))
      if (siteData.description) formData.append('description', siteData.description)
      
      // Optional coordinates - use database column names
      if (siteData.latitude) formData.append('sample_pt_latitude', parseFloat(siteData.latitude))
      if (siteData.longitude) formData.append('sample_pt_longitude', parseFloat(siteData.longitude))
      
      // Required fields with sensible defaults (use lowercase field names as per Django model)
      formData.append('status', 'active')      // Default status
      formData.append('demo', 0)               // Not a demo site
      formData.append('deleted', 0)            // Not deleted
      formData.append('display_image', 0)      // Don't display image by default
      formData.append('publicportal', 0)       // NOT publicPortal - must be lowercase
      formData.append('publiczoom', 13)        // NOT publicZoom - must be lowercase, default 13
      formData.append('zoom', 19)              // Default map zoom
      formData.append('autogps', 0)            // NOT autoGPS - must be lowercase
      formData.append('public_default', 0)     // Not public default

      const token = getToken()
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/site/`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type, axios will set it with boundary for FormData
          },
        }
      )
      
      console.log(`[projectsApi] Created site for project ${projectId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating site:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Update an existing site (name, description, coordinates, Use Device GPS).
   * PATCH /api/project/site/<siteId>/ on the same SiteViewSet createSite uses.
   * Only the provided fields are sent (partial update).
   */
  async updateSite(siteId, siteData) {
    try {
      const fd = new FormData()
      if (siteData.name !== undefined) fd.append('name', siteData.name)
      if (siteData.description !== undefined) fd.append('description', siteData.description ?? '')
      if (siteData.autogps !== undefined) fd.append('autogps', siteData.autogps ? 1 : 0)
      // Coordinates: send when provided; '' clears them (lets Device GPS own them).
      if (siteData.latitude !== undefined)
        fd.append('sample_pt_latitude', siteData.latitude === '' ? '' : parseFloat(siteData.latitude))
      if (siteData.longitude !== undefined)
        fd.append('sample_pt_longitude', siteData.longitude === '' ? '' : parseFloat(siteData.longitude))
      const token = getToken()
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/project/site/${siteId}/`,
        fd,
        { headers: { 'Authorization': token ? `Bearer ${token}` : '' } },
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error updating site:', {
        siteId, status: error.response?.status, data: error.response?.data, message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch the parameter configuration for one device — sensors + their
   * parameters with available unit options + every editable field the
   * Parameter Configuration modal needs.
   *
   * GET /api/data/parameter-config/?device=<id>
   */
  async fetchParameterConfig(deviceId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/data/parameter-config/`,
        { headers: getAuthHeaders(), params: { device: deviceId }, withCredentials: true }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchParameterConfig] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Batch-save parameter configuration changes for a device.
   *
   * POST /api/data/parameter-config/save/
   *   body: { device_id, sensors: [{id, hidden}], params: [{...}] }
   */
  async saveParameterConfig(payload) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/data/parameter-config/save/`,
        payload,
        { headers: getAuthHeaders(), withCredentials: true }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.saveParameterConfig] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch the polar histogram backing a Wind Rose widget.
   *
   * @param {number} directionParamId - Parameter ID for wind direction (degrees)
   * @param {number} speedParamId     - Parameter ID for wind speed (m/s)
   * @param {Object} options          - { fromDate, toDate, timezone }
   * @returns {Promise<Object>} `{ sectors, total_paired_samples, dominant_sector, speed_unit, from_date, to_date }`
   */
  async fetchWindRoseData(directionParamId, speedParamId, options = {}) {
    try {
      const params = {
        direction_param_id: directionParamId,
        speed_param_id: speedParamId,
      }
      if (options.fromDate) params.from_date = options.fromDate
      if (options.toDate)   params.to_date   = options.toDate
      if (options.timezone) params.timezone  = options.timezone

      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/data/wind-rose/`,
        {
          headers: getAuthHeaders(),
          params,
          withCredentials: true,
        }
      )
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchWindRoseData] Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch graph data for a parameter
   * @param {number} parameterId - Parameter ID
   * @param {Object} options - Optional parameters
   * @param {string} options.fromDate - Start date (YYYY-MM-DD HH:MM:SS)
   * @param {string} options.toDate - End date (YYYY-MM-DD HH:MM:SS)
   * @param {string} options.timezone - User timezone
   * @returns {Promise<Object>} Graph data with series, units, and metadata
   */
  async fetchGraphData(parameterId, options = {}) {
    try {
      const params = {
        parameter_id: parameterId,
      }

      if (options.fromDate) params.from_date = options.fromDate
      if (options.toDate) params.to_date = options.toDate
      if (options.timezone) params.timezone = options.timezone
      // Server-side aggregation for long ranges: interval=hour|day|week|month, agg=avg|min|max.
      if (options.interval) params.interval = options.interval
      if (options.agg) params.agg = options.agg
      // Validate curation screen passes includeHidden=true so it can still see
      // (and un-mark) readings the project has Hidden. Everywhere else omits it,
      // so the server applies the per-project validation-status visibility rule.
      if (options.includeHidden) params.include_hidden = 'true'
      // Hard cache-buster — guarantees every request URL is unique so no
      // layer (browser HTTP cache, service worker, CDN, nginx proxy) can
      // serve a stale response. Cache-Control headers alone weren't enough
      // for some users; this makes caching impossible by definition.
      params._t = Date.now()

      const fullUrl = `${API_URL}${API_BASE_PATH}/data/graph-data/`
      console.log(`[projectsApi.fetchGraphData] Making request:`, {
        url: fullUrl,
        params,
        headers: getAuthHeaders()
      })

      const response = await axios.get(
        fullUrl,
        {
          // Cache-busting headers — without these the browser HTTP cache
          // (or any in-line proxy / nginx layer) was returning stale
          // graph-data responses, so values that had since changed in the
          // DB (clears, corrections, re-imports) kept appearing in the
          // grid and chart even after a refresh.
          headers: {
            ...getAuthHeaders(),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma':        'no-cache',
            'Expires':       '0',
          },
          params,
          withCredentials: true,  // Send cookies with the request
        }
      )
      
      console.log(`[projectsApi.fetchGraphData] Response received for parameter ${parameterId}:`, {
        status: response.status,
        dataLength: response.data?.data?.length,
        hasError: response.data?.error,
        data: response.data
      })
      return response.data
    } catch (error) {
      console.error('[projectsApi.fetchGraphData] Error fetching graph data:', {
        parameterId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        message: error.message,
        url: error.config?.url,
        params: error.config?.params
      })
      throw error
    }
  },

  /**
   * Create a new device for a project
   * @param {number} projectId - Project ID
   * @param {Object} deviceData - Device data
   * @param {string} deviceData.name - Device name (required)
   * @param {string} deviceData.uid - Device UID (optional, auto-generated if not provided)
   * @param {number} deviceData.site_id - Site ID (optional)
   * @returns {Promise<Object>} Created device object
   */
  async createDevice(projectId, deviceData) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/device/create/`,
        deviceData,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Created device for project ${projectId}:`, response.data, 'Status:', response.status)
      
      // Handle different response formats:
      // - 201 Created might have minimal/empty body
      // - Backend might wrap in { device: {} } or return device directly
      // - Ensure we always return something usable
      if (response.data && typeof response.data === 'object') {
        return response.data
      } else if (response.status === 201) {
        // 201 Created with empty body - return minimal object
        console.warn('[projectsApi] 201 Created with empty response body')
        return {
          id: deviceData.id || Math.random(),
          name: deviceData.name,
          status: 'created'
        }
      } else {
        return response.data || {}
      }
    } catch (error) {
      console.error('[projectsApi] Error creating device:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Assign or unassign a device to/from a site
   * @param {number} deviceId - Device ID
   * @param {number|null} siteId - Site ID or null to unassign
   * @returns {Promise<Object>} Updated device object
   */
  async assignDeviceToSite(deviceId, siteId) {
    try {
      const response = await axios.put(
        `${API_URL}${API_BASE_PATH}/project/device/${deviceId}/assign/`,
        { site_id: siteId },
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Assigned device ${deviceId} to site ${siteId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error assigning device:', {
        deviceId,
        siteId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch unassigned devices for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} List of unassigned devices
   */
  async fetchUnassignedDevices(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/unassigned-devices/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Fetched unassigned devices for project ${projectId}:`, response.data)
      return response.data.devices || []
    } catch (error) {
      console.error('[projectsApi] Error fetching unassigned devices:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Fetch dashboards for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Array>} List of dashboards
   */
  async fetchDashboards(projectId) {
    try {
      const response = await axios.get(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboards/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Fetched dashboards for project ${projectId}:`, response.data)
      return response.data.dashboards || response.data || []
    } catch (error) {
      console.error('[projectsApi] Error fetching dashboards:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Create a new dashboard for a project
   * @param {number} projectId - Project ID
   * @param {Object} dashboardData - Dashboard data
   * @param {string} dashboardData.name - Dashboard name (required)
   * @param {string} dashboardData.template - Dashboard template type (optional: 'blank', 'overview', etc.)
   * @returns {Promise<Object>} Created dashboard
   */
  async createDashboard(projectId, dashboardData) {
    try {
      const response = await axios.post(
        `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/create/`,
        dashboardData,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Created dashboard for project ${projectId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error creating dashboard:', {
        projectId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Archive a dashboard
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async archiveDashboard(dashboardId) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/archive/`,
        {},
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Archived dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error archiving dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Reactivate an archived dashboard
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async reactivateDashboard(dashboardId) {
    try {
      const response = await axios.patch(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/reactivate/`,
        {},
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Reactivated dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error reactivating dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  /**
   * Delete a dashboard permanently
   * @param {number} dashboardId - Dashboard ID
   * @returns {Promise<Object>} Response
   */
  async deleteDashboard(dashboardId) {
    try {
      const response = await axios.delete(
        `${API_URL}${API_BASE_PATH}/dashboard/${dashboardId}/`,
        {
          headers: getAuthHeaders(),
        }
      )
      
      console.log(`[projectsApi] Deleted dashboard ${dashboardId}:`, response.data)
      return response.data
    } catch (error) {
      console.error('[projectsApi] Error deleting dashboard:', {
        dashboardId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      })
      throw error
    }
  },

  // ── Project user management ────────────────────────────────────────────────
  // GET    /api/project/<projectId>/users/
  // POST   /api/project/<projectId>/users/add/         { email, access }
  // DELETE /api/project/<projectId>/users/<accountId>/
  //
  // Mirrors the legacy Symfony changeadmin / addcollaborator /
  // removecollaborator / deleteadmin endpoints. Returns { data: [...] }
  // shaped as { id, name, username, email, access } where access is one
  // of 'Owner' | 'Admin' | 'Collaborator'.

  async listProjectUsers(projectId) {
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async addProjectUser(projectId, { email, access }) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/add/`,
      { email, access },
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async removeProjectUser(projectId, accountId) {
    const response = await axios.delete(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/users/${accountId}/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  // ── Project dashboard archive / restore (project-scoped) ───────────────────
  // Backed by TemplateAssignment.is_enabled. Archive hides the dashboard from
  // the project's nav without deleting the underlying template.

  async archiveProjectDashboard(projectId, templateId) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/${templateId}/archive/`,
      {},
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async restoreProjectDashboard(projectId, templateId) {
    const response = await axios.post(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboard/${templateId}/restore/`,
      {},
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async listArchivedDashboards(projectId) {
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/dashboards/archived/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  // ── Embedded Data Applet ────────────────────────────────────────
  // Owner/admin read + write of the applet's embed flag and selected
  // parameters. Persisted server-side (Applet / applet_parameters) so
  // the public embed (/project/applet/text|html/<id>) can render them.
  async getAppletConfig(projectId) {
    const response = await axios.get(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/applet/config/`,
      { headers: getAuthHeaders() },
    )
    return response.data
  },

  async saveAppletConfig(projectId, { allow_embed, parameter_ids }) {
    const response = await axios.put(
      `${API_URL}${API_BASE_PATH}/project/${projectId}/applet/config/`,
      { allow_embed: !!allow_embed, parameter_ids: parameter_ids || [] },
      { headers: getAuthHeaders() },
    )
    return response.data
  },
}

// ─── Live → Sensor Readings streaming (device command 81) ────────────────────
// Start/stop the logger's CBOR sensor stream and poll the latest decoded values.
// opts (enable): { rate_sec?, sens_uid?, inactivity_timeout_sec? }.
export async function enableLiveStream(projectId, deviceId, opts = {}) {
  const resp = await axios.post(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/live-stream/`,
    { enable: true, ...opts },
    { headers: getAuthHeaders() },
  )
  return resp.data
}

export async function disableLiveStream(projectId, deviceId) {
  const resp = await axios.post(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/live-stream/`,
    { enable: false },
    { headers: getAuthHeaders() },
  )
  return resp.data
}

export async function fetchLiveReadings(projectId, deviceId) {
  const resp = await axios.get(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/live-readings/`,
    { headers: getAuthHeaders() },
  )
  return resp.data
}

// ─── Live → Communications (comm info poll) ──────────────────────────────────
// Latest cellular/comms info written by the MQTT bridge. IMEI/ICCID come back
// masked ("*****") for collaborators; RSRQ is null for Wi-Fi/Iridium.
export async function fetchCommInfo(projectId, deviceId) {
  const resp = await axios.get(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/comm-info/`,
    { headers: getAuthHeaders() },
  )
  return resp.data
}

// ─── Live → Iridium Messages ─────────────────────────────────────────────────
// Paginated list of the device's Iridium satellite messages (matched by device
// FK, falling back to IMEI). Shown only for Iridium loggers (device.iridium).
export async function fetchIridiumMessages(projectId, deviceId, { page = 1, perPage = 25, search = '' } = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
  if (search) params.set('search', search)
  const resp = await axios.get(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/iridium-messages/?${params.toString()}`,
    { headers: getAuthHeaders() },
  )
  return resp.data
}

// ─── Firmware update progress poll (device command 80 / fwprogress) ───────────
export async function fetchFirmwareProgress(projectId, deviceId) {
  const resp = await axios.get(
    `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}/firmware/progress/`,
    { headers: getAuthHeaders() },
  )
  return resp.data
}
